import { apiFetch } from "./client";
import { normalizePlacedOrder, type PlacedOrder } from "./purchase_order_lifecycle";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";
import type { PurchaseOrderEmailAttachment } from "./purchase_orders";

export type SavedDraftReadiness =
  | "ready"
  | "needs_review"
  | "source_changed";
export type SavedDraftSourceStatus =
  | "draft"
  | "ordered"
  | "archived"
  | "unknown";
export type SavedDraftSourceStatusProvenance = "observed" | "inferred";
export type SavedDraftTaxResolution = "unresolved" | "rate" | "none";
declare const savedDraftSourceIdBrand: unique symbol;
export type SavedDraftSourceId = string & {
  readonly [savedDraftSourceIdBrand]: true;
};

export interface SavedDraftBlocker {
  code: string;
  message: string;
  item_id: StockyPublicBigIntId | null;
  /** False means the merchant may continue after reviewing the warning. */
  blocking: boolean;
}

/**
 * One row in the normal purchase-order screen. `id === null` is an immutable
 * Stocky/CSV source which has not been adopted yet; an id means the same source
 * already owns one native working draft. The source id stays opaque and native
 * ids are canonical decimal strings, so no browser Number conversion can
 * change either identity.
 */
export interface SavedDraftSummary {
  id: StockyPublicBigIntId | null;
  revision: number | null;
  status: "candidate" | "draft";
  source_id: SavedDraftSourceId;
  /** Stable receipt identity; never use the mutable migrated-row primary key. */
  source_identity: string;
  source_sha: string;
  source_number: string | null;
  source_status: SavedDraftSourceStatus;
  source_status_provenance: SavedDraftSourceStatusProvenance;
  supplier_id: StockyPublicBigIntId | null;
  supplier_name: string | null;
  currency: string | null;
  item_count: number;
  readiness: SavedDraftReadiness;
  blockers: SavedDraftBlocker[];
  updated_at: string | null;
}

export interface SavedDraftItem {
  id: StockyPublicBigIntId;
  variant_id: StockyPublicBigIntId | null;
  sku: string | null;
  title: string | null;
  quantity: number;
  unit_price: string;
  unit_price_known: boolean;
  tax_resolution: SavedDraftTaxResolution;
  tax_rate_id: StockyPublicBigIntId | null;
  tax_rate_name: string | null;
  tax_rate: string | null;
  /** False for retained snapshot-only lines which cannot change Shopify stock. */
  inventory_effect_available: boolean;
}

export interface SavedDraftTaxRateOption {
  id: StockyPublicBigIntId;
  name: string;
  rate: string;
}

export interface SavedPurchaseOrderDraft
  extends Omit<SavedDraftSummary, "id" | "revision" | "status"> {
  id: StockyPublicBigIntId;
  revision: number;
  status: "draft";
  issue_date: string | null;
  reference_number: string | null;
  location_id: StockyPublicBigIntId | null;
  location_name: string | null;
  expected_at: string | null;
  note: string | null;
  items: SavedDraftItem[];
  tax_rates: SavedDraftTaxRateOption[];
  email_state: "none" | "in_flight" | "unknown" | "sent";
  last_delivery_id: StockyPublicBigIntId | null;
}

export interface SavedDraftItemInput {
  id?: StockyPublicBigIntId;
  variant_id?: StockyPublicBigIntId | null;
  sku: string | null;
  title: string | null;
  quantity: number;
  unit_price: string;
  tax_resolution: SavedDraftTaxResolution;
  tax_rate_id?: StockyPublicBigIntId | null;
  tax_rate_name?: string | null;
  tax_rate?: string | null;
}

export interface SavedDraftUpdateInput {
  supplier_id: StockyPublicBigIntId | null;
  issue_date: string | null;
  reference_number: string | null;
  location_id: StockyPublicBigIntId | null;
  currency: string | null;
  expected_at: string | null;
  note: string | null;
  items: SavedDraftItemInput[];
}

export interface SavedDraftEmailPreview {
  draft_sha: string;
  revision: number;
  from_email: string;
  to_email: string;
  subject: string;
  text: string;
  attachments: PurchaseOrderEmailAttachment[];
  include_purchase_order_pdf: boolean;
}

export interface SavedDraftEmailSendResult {
  sent: boolean;
  replayed: boolean;
  to_email: string;
  delivery_id: StockyPublicBigIntId;
  status: string;
}

function requireObject(value: unknown, label: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${label} response is invalid`);
  }
  return value as Record<string, unknown>;
}

function nullableId(value: unknown, label: string): StockyPublicBigIntId | null {
  if (value === null || value === undefined) return null;
  try {
    return canonicalStockyPublicBigIntId(value);
  } catch {
    throw new TypeError(`${label} is invalid`);
  }
}

function requireId(value: unknown, label: string): StockyPublicBigIntId {
  const id = nullableId(value, label);
  if (id === null) throw new TypeError(`${label} is missing`);
  return id;
}

function requireRevision(value: unknown): number {
  if (!Number.isSafeInteger(value) || Number(value) < 1) {
    throw new TypeError("purchase order revision is invalid");
  }
  return Number(value);
}

function nullableString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function requireSha(value: unknown, label: string): string {
  if (typeof value !== "string" || !/^[a-f0-9]{64}$/i.test(value)) {
    throw new TypeError(`${label} is invalid`);
  }
  return value.toLowerCase();
}

/**
 * Preserve the unified Stocky-data public id byte-for-byte; never Number it.
 * Stocky-migrated purchase orders occupy the even side of the unified
 * namespace. Native UreyukiBox purchase orders are odd and must never be sent
 * to the Stocky-source endpoint.
 */
export function canonicalSavedDraftSourceId(value: unknown): SavedDraftSourceId {
  if (
    typeof value !== "string" ||
    value.length > 96 ||
    !/^purchase-order:[1-9][0-9]*$/.test(value) ||
    !/[02468]$/.test(value)
  ) {
    throw new TypeError("saved draft source_id is invalid");
  }
  return value as SavedDraftSourceId;
}

function normalizeBlocker(value: unknown): SavedDraftBlocker {
  const blocker = requireObject(value, "saved draft blocker");
  if (
    typeof blocker.code !== "string" ||
    typeof blocker.blocking !== "boolean"
  ) {
    throw new TypeError("saved draft blocker code is invalid");
  }
  return {
    code: blocker.code,
    message:
      typeof blocker.message === "string" ? blocker.message : blocker.code,
    item_id: nullableId(blocker.item_id, "saved draft blocker item_id"),
    blocking: blocker.blocking,
  };
}

function normalizeReadiness(value: unknown): SavedDraftReadiness {
  if (
    value === "ready" ||
    value === "needs_review" ||
    value === "source_changed"
  ) {
    return value;
  }
  throw new TypeError("saved draft readiness is invalid");
}

function normalizeSourceStatus(value: unknown): SavedDraftSourceStatus {
  return value === "draft" ||
    value === "ordered" ||
    value === "archived" ||
    value === "unknown"
    ? value
    : "unknown";
}

function normalizeSummary(value: unknown): SavedDraftSummary {
  const summary = requireObject(value, "saved draft summary");
  const id = nullableId(summary.id, "saved draft id");
  const revision =
    summary.revision === null || summary.revision === undefined
      ? null
      : requireRevision(summary.revision);
  if ((id === null) !== (revision === null)) {
    throw new TypeError("saved draft identity is incomplete");
  }
  if (!Array.isArray(summary.blockers)) {
    throw new TypeError("saved draft blockers response is invalid");
  }
  const blockers = summary.blockers.map(normalizeBlocker);
  const itemCount = summary.item_count;
  if (!Number.isSafeInteger(itemCount) || Number(itemCount) < 0) {
    throw new TypeError("saved draft item_count is invalid");
  }
  const provenance = summary.source_status_provenance;
  if (provenance !== "observed" && provenance !== "inferred") {
    throw new TypeError("saved draft status provenance is invalid");
  }
  return {
    id,
    revision,
    status: id === null ? "candidate" : "draft",
    source_id: canonicalSavedDraftSourceId(summary.source_id),
    source_identity:
      typeof summary.source_identity === "string" &&
      summary.source_identity.trim() !== ""
        ? summary.source_identity
        : canonicalSavedDraftSourceId(summary.source_id),
    source_sha: requireSha(summary.source_sha, "saved draft source_sha"),
    source_number: nullableString(summary.source_number),
    source_status: normalizeSourceStatus(summary.source_status),
    source_status_provenance: provenance,
    supplier_id: nullableId(summary.supplier_id, "saved draft supplier_id"),
    supplier_name: nullableString(summary.supplier_name),
    currency: nullableString(summary.currency),
    item_count: Number(itemCount),
    readiness: normalizeReadiness(summary.readiness),
    blockers,
    updated_at: nullableString(summary.updated_at),
  };
}

function normalizeItem(value: unknown): SavedDraftItem {
  const item = requireObject(value, "saved draft item");
  if (!Number.isSafeInteger(item.quantity)) {
    throw new TypeError("saved draft item quantity is invalid");
  }
  if (typeof item.unit_price !== "string") {
    throw new TypeError("saved draft item unit_price is invalid");
  }
  if (typeof item.unit_price_known !== "boolean") {
    throw new TypeError("saved draft item unit_price_known is invalid");
  }
  if (typeof item.inventory_effect_available !== "boolean") {
    throw new TypeError(
      "saved draft item inventory_effect_available is invalid",
    );
  }
  const taxResolution = item.tax_resolution;
  if (
    taxResolution !== "unresolved" &&
    taxResolution !== "rate" &&
    taxResolution !== "none"
  ) {
    throw new TypeError("saved draft item tax_resolution is invalid");
  }
  const variantId = nullableId(item.variant_id, "saved draft item variant_id");
  return {
    id: requireId(item.id, "saved draft item id"),
    variant_id: variantId,
    sku: nullableString(item.sku),
    title: nullableString(item.title),
    quantity: Number(item.quantity),
    unit_price: item.unit_price,
    unit_price_known: item.unit_price_known,
    tax_resolution: taxResolution,
    tax_rate_id: nullableId(item.tax_rate_id, "saved draft item tax_rate_id"),
    tax_rate_name: nullableString(item.tax_rate_name),
    tax_rate: nullableString(item.tax_rate),
    inventory_effect_available: item.inventory_effect_available,
  };
}

function normalizeTaxRate(value: unknown): SavedDraftTaxRateOption {
  const rate = requireObject(value, "saved draft tax rate");
  if (typeof rate.name !== "string" || typeof rate.rate !== "string") {
    throw new TypeError("saved draft tax rate is invalid");
  }
  return {
    id: requireId(rate.id, "saved draft tax rate id"),
    name: rate.name,
    rate: rate.rate,
  };
}

export function normalizeSavedPurchaseOrderDraft(
  value: unknown,
): SavedPurchaseOrderDraft {
  const draft = requireObject(value, "saved draft");
  const summary = normalizeSummary(draft);
  if (summary.id === null || summary.revision === null) {
    throw new TypeError("saved draft is not materialized");
  }
  if (!Array.isArray(draft.items)) {
    throw new TypeError("saved draft items response is invalid");
  }
  const emailState = draft.email_state;
  if (
    emailState !== "none" &&
    emailState !== "in_flight" &&
    emailState !== "unknown" &&
    emailState !== "sent"
  ) {
    throw new TypeError("saved draft email state is invalid");
  }
  return {
    ...summary,
    id: summary.id,
    revision: summary.revision,
    status: "draft",
    issue_date: nullableString(draft.issue_date),
    reference_number: nullableString(draft.reference_number),
    location_id: nullableId(draft.location_id, "saved draft location_id"),
    location_name: nullableString(draft.location_name),
    expected_at: nullableString(draft.expected_at),
    note: nullableString(draft.note),
    items: draft.items.map(normalizeItem),
    tax_rates: Array.isArray(draft.tax_rates)
      ? draft.tax_rates.map(normalizeTaxRate)
      : [],
    email_state: emailState,
    last_delivery_id: nullableId(
      draft.last_delivery_id,
      "saved draft last_delivery_id",
    ),
  };
}

function canonicalItemInput(item: SavedDraftItemInput): SavedDraftItemInput {
  return {
    ...(item.id === undefined
      ? {}
      : { id: canonicalStockyPublicBigIntId(item.id) }),
    variant_id:
      item.variant_id === null || item.variant_id === undefined
        ? null
        : canonicalStockyPublicBigIntId(item.variant_id),
    sku: item.sku,
    title: item.title,
    quantity: item.quantity,
    unit_price: item.unit_price,
    tax_resolution: item.tax_resolution,
    tax_rate_id:
      item.tax_rate_id === null || item.tax_rate_id === undefined
        ? null
        : canonicalStockyPublicBigIntId(item.tax_rate_id),
    tax_rate_name: item.tax_rate_name ?? null,
    tax_rate: item.tax_rate ?? null,
  };
}

function revisionEtag(revision: number): string {
  return `"rev-${requireRevision(revision)}"`;
}

export async function listSavedPurchaseOrderDrafts(
  sourceId?: SavedDraftSourceId,
): Promise<{
  drafts: SavedDraftSummary[];
  truncated: boolean;
}> {
  const path =
    sourceId === undefined
      ? "/api/purchase-orders/saved-drafts"
      : `/api/purchase-orders/saved-drafts?${new URLSearchParams({
          source_id: canonicalSavedDraftSourceId(sourceId),
        }).toString()}`;
  const value = requireObject(
    await apiFetch<unknown>(path),
    "saved draft list",
  );
  if (!Array.isArray(value.drafts)) {
    throw new TypeError("saved draft list response is invalid");
  }
  return {
    drafts: value.drafts.map(normalizeSummary),
    truncated: value.truncated === true,
  };
}

export async function materializeSavedPurchaseOrderDraft(
  sourceId: SavedDraftSourceId,
  sourceSha: string,
  idempotencyKey: string,
): Promise<SavedPurchaseOrderDraft> {
  const canonicalSourceId = canonicalSavedDraftSourceId(sourceId);
  const value = await apiFetch<unknown>(
    `/api/purchase-orders/saved-drafts/from-stocky/${encodeURIComponent(canonicalSourceId)}`,
    {
      method: "POST",
      headers: {
        "Idempotency-Key": idempotencyKey,
        "If-Match": `"${requireSha(sourceSha, "saved draft source_sha")}"`,
      },
    },
  );
  return normalizeSavedPurchaseOrderDraft(value);
}

export async function getSavedPurchaseOrderDraft(
  id: StockyPublicBigIntId,
): Promise<SavedPurchaseOrderDraft> {
  const value = await apiFetch<unknown>(
    `/api/purchase-orders/saved-drafts/${encodeURIComponent(canonicalStockyPublicBigIntId(id))}`,
  );
  return normalizeSavedPurchaseOrderDraft(value);
}

export async function updateSavedPurchaseOrderDraft(
  id: StockyPublicBigIntId,
  revision: number,
  input: SavedDraftUpdateInput,
): Promise<SavedPurchaseOrderDraft> {
  const value = await apiFetch<unknown>(
    `/api/purchase-orders/saved-drafts/${encodeURIComponent(canonicalStockyPublicBigIntId(id))}`,
    {
      method: "PUT",
      headers: { "If-Match": revisionEtag(revision) },
      body: {
        ...input,
        supplier_id:
          input.supplier_id === null
            ? null
            : canonicalStockyPublicBigIntId(input.supplier_id),
        location_id:
          input.location_id === null
            ? null
            : canonicalStockyPublicBigIntId(input.location_id),
        items: input.items.map(canonicalItemInput),
      },
    },
  );
  return normalizeSavedPurchaseOrderDraft(value);
}

export async function getSavedDraftEmailPreview(
  id: StockyPublicBigIntId,
): Promise<SavedDraftEmailPreview> {
  const value = requireObject(
    await apiFetch<unknown>(
      `/api/purchase-orders/saved-drafts/${encodeURIComponent(canonicalStockyPublicBigIntId(id))}/email-draft`,
    ),
    "saved draft email preview",
  );
  if (
    typeof value.from_email !== "string" ||
    typeof value.to_email !== "string" ||
    typeof value.subject !== "string" ||
    typeof value.text !== "string"
  ) {
    throw new TypeError("saved draft email preview response is invalid");
  }
  const rawAttachments = value.attachments ?? [];
  if (
    !Array.isArray(rawAttachments) ||
    rawAttachments.some(
      (attachment) =>
        attachment === null ||
        typeof attachment !== "object" ||
        Array.isArray(attachment) ||
        typeof (attachment as Record<string, unknown>).filename !== "string" ||
        typeof (attachment as Record<string, unknown>).content_type !== "string" ||
        typeof (attachment as Record<string, unknown>).content !== "string",
    ) ||
    (value.include_purchase_order_pdf !== undefined &&
      typeof value.include_purchase_order_pdf !== "boolean")
  ) {
    throw new TypeError("saved draft email preview response is invalid");
  }
  return {
    draft_sha: requireSha(value.draft_sha, "saved draft email sha"),
    revision: requireRevision(value.revision),
    from_email: value.from_email,
    to_email: value.to_email,
    subject: value.subject,
    text: value.text,
    attachments: rawAttachments as PurchaseOrderEmailAttachment[],
    // Older servers did not expose the selection because the current client
    // did not yet support review attachments.  New previews default the
    // server-rendered PDF on, while the accepted POST always carries the
    // merchant's explicit final choice.
    include_purchase_order_pdf: value.include_purchase_order_pdf ?? true,
  };
}

export async function emailSavedPurchaseOrderDraft(
  id: StockyPublicBigIntId,
  preview: SavedDraftEmailPreview,
  idempotencyKey: string,
): Promise<SavedDraftEmailSendResult> {
  const value = requireObject(
    await apiFetch<unknown>(
      `/api/purchase-orders/saved-drafts/${encodeURIComponent(canonicalStockyPublicBigIntId(id))}/email`,
      {
        method: "POST",
        headers: {
          "Idempotency-Key": idempotencyKey,
          "If-Match": `"${requireSha(preview.draft_sha, "saved draft email sha")}"`,
        },
        body: {
          draft_sha: preview.draft_sha,
          email_snapshot: {
            to_email: preview.to_email,
            subject: preview.subject,
            text: preview.text,
            attachments: preview.attachments,
            include_purchase_order_pdf: preview.include_purchase_order_pdf,
          },
        },
      },
    ),
    "saved draft email result",
  );
  if (
    typeof value.sent !== "boolean" ||
    typeof value.replayed !== "boolean" ||
    typeof value.to_email !== "string" ||
    typeof value.status !== "string"
  ) {
    throw new TypeError("saved draft email result response is invalid");
  }
  return {
    sent: value.sent,
    replayed: value.replayed,
    to_email: value.to_email,
    delivery_id: requireId(value.delivery_id, "saved draft delivery_id"),
    status: value.status,
  };
}

export async function placeSavedPurchaseOrderDraft(
  id: StockyPublicBigIntId,
  revision: number,
  idempotencyKey: string,
): Promise<PlacedOrder> {
  const value = await apiFetch<unknown>(
    `/api/purchase-orders/saved-drafts/${encodeURIComponent(canonicalStockyPublicBigIntId(id))}/place`,
    {
      method: "POST",
      headers: {
        "Idempotency-Key": idempotencyKey,
        "If-Match": revisionEtag(revision),
      },
    },
  );
  return normalizePlacedOrder(value);
}
